// Tela do Log de Auditoria (issue #42).
//
// É a única tela que NÃO lê do Snapshot: o log é append-only, cresce para
// sempre e é consultado por página, com filtro resolvido em SQL no servidor
// (ADR-0012). A paginação, porém, é a mesma das outras telas — a
// `tabelaPaginada` recebe `totalExterno` e devolve o rodapé padrão.
//
// Só existe no modo hospedado — quem grava o log é o servidor (ADR-0010).

import { apiAuditoria, type RespostaAuditoria } from "../api.ts";
import { formatData } from "../../domain/datas.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, tabelaPaginada, botao } from "../ui.ts";
import { barraFiltros, filtroData, telaLista } from "./_cadastro.ts";
import { linhaLegivel, moduloLegivel } from "./_auditoria-legivel.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { filtroMultiplo } from "./_lista-filtros.ts";

function dataHora(iso: string): string {
  const d = iso.slice(0, 10);
  const h = iso.slice(11, 16);
  return `${formatData(d)} ${h}`;
}

export function renderAuditoria(container: HTMLElement, store: Store): () => void {
  let dados: RespostaAuditoria | null = null;
  let carregando = false;
  let erro = "";

  // filtros e página num estado só (#93): mudar um filtro volta à página 1 e
  // busca de novo no servidor. A ordenação é do servidor (mais recente
  // primeiro) e o tamanho da página também — `ordCol` fica nulo.
  const lista = new EstadoLista({ usuario: null as string[] | null, acao: null as string[] | null, de: "", ate: "" });
  const est = lista.tabela;
  const fUsuario = filtroMultiplo(lista, "usuario", "Usuário", () => buscar());
  const fAcao = filtroMultiplo(lista, "acao", "Ação", () => buscar());

  const buscar = (): void => {
    if (!__HOSTED__) return;
    carregando = true;
    erro = "";
    render();
    void apiAuditoria({
      de: lista.filtro("de"),
      ate: lista.filtro("ate"),
      pagina: est.page,
      porPagina: est.size,
    }).then(
      (r) => {
        dados = r;
        est.size = r.porPagina;
        carregando = false;
        render();
      },
      (e: unknown) => {
        erro = e instanceof Error ? e.message : "Falha ao carregar.";
        carregando = false;
        render();
      },
    );
  };

  const render = (): void => {
    clear(container);
    const snap = store.get();

    if (!__HOSTED__) {
      container.append(
        el(
          "p",
          { class: "muted lista-vazia" },
          "A auditoria está disponível só na versão hospedada — quem grava o log é o servidor.",
        ),
      );
      return;
    }

    const usuarios = [...snap.usuarios].sort((a, b) =>
      a.nome.localeCompare(b.nome, "pt-BR"),
    );
    const nomeUsuario = new Map(usuarios.map((u) => [u.id, u.nome]));

    fUsuario.atualizar([
      { id: "__sistema__", nome: "Sistema" },
      ...usuarios.map((u) => ({ id: u.id, nome: u.nome })),
    ]);

    const modulos = [...(dados?.agregados ?? [])]
      .map((a) => [a, moduloLegivel(a)] as const)
      .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));

    fAcao.atualizar(modulos.map(([id, nome]) => ({ id, nome })));

    const barra = barraFiltros(
      [
        fUsuario.element,
        fAcao.element,
        filtroData("De", lista.filtro("de"), (v) => {
          lista.mudarFiltro("de", v);
          buscar();
        }),
        filtroData("Até", lista.filtro("ate"), (v) => {
          lista.mudarFiltro("ate", v);
          buscar();
        }),
      ],
      () => {
        lista.limpar();
        buscar();
      },
    );

    const corpo: Node[] = [];
    if (erro) {
      corpo.push(el("p", { class: "form-erro" }, erro));
    } else if (carregando && dados == null) {
      corpo.push(el("p", { class: "muted lista-vazia" }, "Carregando…"));
    } else if (dados == null || dados.linhas.length === 0) {
      corpo.push(
        el("p", { class: "muted lista-vazia" }, lista.mensagemVazio({ nenhum: "Nenhum registro.", filtrado: "Nenhum registro com esses filtros." })),
      );
    } else {
      const filtroUsuario = fUsuario.selecionados();
      const filtroAcao = fAcao.selecionados();
      const linhasFiltradas = dados.linhas
        .filter((l) => filtroUsuario.size === 0 || (l.usuarioId != null && filtroUsuario.has(l.usuarioId)) || (l.usuarioId == null && filtroUsuario.has("__sistema__")))
        .filter((l) => filtroAcao.size === 0 || filtroAcao.has(l.resumo));
      const linhas = linhasFiltradas.map((l) => {
        const leg = linhaLegivel(snap, l.resumo, l.detalhe);
        const ver = botao("secundario", {}, "Ver JSON");
        ver.addEventListener("click", () => abrirJson(l.resumo, l.detalhe));
        return el(
          "tr",
          leg.erro ? { class: "linha-erro" } : {},
          el("td", {}, dataHora(l.em)),
          el(
            "td",
            {},
            l.usuarioId == null
              ? el("span", { class: "muted" }, "sistema")
              : (nomeUsuario.get(l.usuarioId) ?? "—"),
          ),
          el("td", {}, leg.modulo),
          el(
            "td",
            {},
            el("b", {}, leg.verbo),
            leg.detalhes ? el("small", { class: "muted" }, ` — ${leg.detalhes}`) : null,
          ),
          el("td", { class: "acoes" }, ver),
        );
      });

      corpo.push(
        tabelaPaginada(
          "auditoria",
          [
            { t: "Quando", ord: false, w: 150 },
            { t: "Quem", ord: false, w: 180 },
            { t: "Ação", ord: false, w: 190 },
            { t: "Descrição", ord: false },
            { t: "", ord: false },
          ],
          linhas,
          est,
          buscar,
          { totalExterno: dados.total },
        ),
      );
    }

    container.append(telaLista(barra, ...corpo));
  };

  buscar();
  render();
  // A tela não assina o store: o log não vem do Snapshot (ADR-0012).
  return () => {};
}

function abrirJson(resumo: string, detalhe: string): void {
  openModal(resumo, () => {
    let texto = detalhe;
    try {
      texto = JSON.stringify(JSON.parse(detalhe), null, 2);
    } catch {
      /* deixa cru se não abrir */
    }
    return el("pre", { class: "json-cru" }, texto);
  });
}
